export const ARROW_LABEL_MAX = 80;

export function normalizeArrowLabelText(...candidates) {
  for (const raw of candidates) {
    const text = String(raw || '').trim().slice(0, ARROW_LABEL_MAX);
    if (text) return text;
  }
  return '';
}

/**
 * One relationship verb on a directed connect line.
 * Prefers meta.label; falls back to legacy end/start chips or mid-path text.
 */
export function readArrowLabel(shape) {
  if (!shape || shape.type !== 'arrow') return '';
  return normalizeArrowLabelText(
    shape.meta?.label,
    shape.meta?.endLabel,
    shape.meta?.startLabel,
    shape.props?.text
  );
}

/** Persist a single label and clear the old dual start/end chips. */
export function arrowLabelMeta(meta, label) {
  const next = String(label ?? '').slice(0, ARROW_LABEL_MAX);
  return {
    ...(meta && typeof meta === 'object' ? meta : {}),
    label: next,
    startLabel: '',
    endLabel: '',
  };
}

function isDirectedBoardArrow(props = {}) {
  return props.arrowheadStart === 'none' && props.arrowheadEnd === 'arrow';
}

function needsAlignedArrow(props = {}) {
  return props.kind !== 'arc' || Number(props.bend) !== 0;
}

/**
 * Default tldraw arrow connector props: one-way arrow, no mid-path text.
 * Dual end labels used to live in shape.meta; the verb now lives in meta.label.
 */
export function boardLinkShapeProps(extraProps = {}) {
  const rest = { ...(extraProps || {}) };
  delete rest.text;
  delete rest.labelPosition;
  delete rest.arrowheadStart;
  delete rest.arrowheadEnd;
  return {
    size: 'm',
    color: 'black',
    ...rest,
    kind: 'arc',
    bend: 0,
    arrowheadStart: 'none',
    arrowheadEnd: 'arrow',
    text: '',
    labelPosition: 0.5,
  };
}

/**
 * Keep new arrows one-way with a single meta.label (no mid-path text).
 *
 * @returns {() => void} unregister
 */
export function registerBoardLinkDefaults(editor) {
  if (!editor?.sideEffects?.registerAfterCreateHandler) return () => {};

  const forceDirectedArrow = (shape, { migrateLabel = false } = {}) => {
    const text = String(shape.props?.text || '').trim();
    const needsHeads = !isDirectedBoardArrow(shape.props);
    const needsClearText = Boolean(text);
    const needsAligned = needsAlignedArrow(shape.props);
    if (!needsHeads && !needsClearText && !needsAligned) return;

    editor.updateShape({
      id: shape.id,
      type: 'arrow',
      meta: migrateLabel || needsClearText
        ? arrowLabelMeta(shape.meta, text || readArrowLabel(shape))
        : shape.meta,
      props: {
        ...shape.props,
        kind: 'arc',
        bend: 0,
        arrowheadStart: 'none',
        arrowheadEnd: 'arrow',
        text: '',
        labelPosition: 0.5,
      },
    });
  };

  const unsubCreate = editor.sideEffects.registerAfterCreateHandler('shape', shape => {
    if (shape.type !== 'arrow') return;
    forceDirectedArrow(shape, { migrateLabel: true });
  });

  const unsubChange = editor.sideEffects.registerAfterChangeHandler?.('shape', (_prev, next) => {
    if (!next || next.type !== 'arrow') return;
    forceDirectedArrow(next);
  });

  return () => {
    unsubCreate?.();
    unsubChange?.();
  };
}

/**
 * Migrate legacy dual/mid-path labels into meta.label, and force a one-way
 * arrowhead so bound offset still works.
 */
export function normalizeExistingBoardLinks(editor) {
  if (!editor) return false;

  const updates = [];
  for (const shape of editor.getCurrentPageShapes?.() || []) {
    if (!shape || shape.type !== 'arrow') continue;

    const legacyText = String(shape.props?.text || '').trim();
    const label = readArrowLabel(shape);
    const storedLabel = String(shape.meta?.label || '').trim();
    const needsMeta = Boolean(label) && storedLabel !== label;
    const needsHead = !isDirectedBoardArrow(shape.props);
    const needsAligned = needsAlignedArrow(shape.props);
    const needsClearText = Boolean(legacyText);
    const needsClearDual = Boolean(
      String(shape.meta?.startLabel || '').trim()
      || String(shape.meta?.endLabel || '').trim()
    );

    if (!needsMeta && !needsHead && !needsClearText && !needsClearDual && !needsAligned) continue;

    updates.push({
      id: shape.id,
      type: 'arrow',
      meta: arrowLabelMeta(shape.meta, label),
      props: {
        ...shape.props,
        kind: 'arc',
        bend: 0,
        arrowheadStart: 'none',
        arrowheadEnd: 'arrow',
        text: '',
        labelPosition: 0.5,
      },
    });
  }

  if (updates.length) editor.updateShapes(updates);
  return updates.length > 0;
}
